import Dexie from 'dexie';
import { encryptData, decryptData, isUserAuthenticated } from '../utils/encryption.js';
import { validateProjectData, sanitizeProjectData } from '../utils/validation.js';
import { getAuth } from 'firebase/auth';

// Feature flag for encryption - can be disabled if issues arise
const ENABLE_ENCRYPTION = false; // Keep disabled for now
const ENABLE_VALIDATION = true;  // Enable validation (very safe)

// Get user-specific database name
const getUserDatabaseName = () => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      // Fallback to a default name if no user is authenticated
      console.warn('No authenticated user found, using default database');
      return 'GridMapPro_Default';
    }
    
    // Use user's UID to create a unique database name
    return `GridMapPro_${user.uid}`;
  } catch (error) {
    console.warn('Error getting user database name:', error);
    return 'GridMapPro_Default';
  }
};

class GridMapDatabase extends Dexie {
  constructor() {
    const dbName = getUserDatabaseName();
    super(dbName);
    
    console.log(`Initializing database: ${dbName}`);
    
    this.version(5).stores({
      projects: '++id, name, customer, customerAddress, customerPhone, type, status, createdAt, updatedAt, encryptedData, mapSnapshot',
      mapState: 'projectId, center, zoom, style, viewport, encryptedData',
      equipment: '[projectId+id], type, coordinates, lngLat, details, notes, properties, encryptedData',
      conductors: '[projectId+id], type, coordinates, wireSize, length, notes, properties, encryptedData'
    });
  }
}

// Create database instance
let db = null;

// Initialize database for current user
const initializeDatabase = () => {
  if (db) {
    // Close existing database connection
    db.close();
  }
  
  db = new GridMapDatabase();
  return db;
};

// Get database instance, initializing if needed
const getDatabase = async () => {
  if (!db) {
    console.log('Database not available, initializing...');
    db = initializeDatabase();
    await db.open();
  } else if (!db.isOpen()) {
    console.log('Database was closed, reopening...');
    try {
      await db.open();
    } catch (error) {
      console.log('Failed to reopen database, reinitializing...');
      db = initializeDatabase();
      await db.open();
    }
  }
  return db;
};

// Switch database when user changes (call this when user logs in/out)
export const switchUserDatabase = async () => {
  console.log('Switching to user-specific database...');
  
  // Close existing database connection if it exists
  if (db) {
    try {
      await db.close();
      console.log('Closed existing database connection');
    } catch (error) {
      console.warn('Error closing existing database:', error);
    }
  }
  
  // Clear the database instance
  db = null;
  
  // Initialize new database for current user
  db = initializeDatabase();
  
  console.log('Database switched successfully');
  return db;
};

// Helper function to encrypt sensitive project data
const encryptProjectData = async (projectData) => {
  if (!ENABLE_ENCRYPTION) return null;
  
  try {
    if (!isUserAuthenticated()) {
      console.warn('User not authenticated, skipping encryption');
      return null;
    }
    
    const sensitiveData = {
      customerAddress: projectData.customerAddress,
      customerPhone: projectData.customerPhone,
      equipment: projectData.equipment,
      conductors: projectData.conductors
    };
    
    return await encryptData(sensitiveData);
  } catch (error) {
    console.warn('Encryption failed, falling back to unencrypted storage:', error);
    return null;
  }
};

// Helper function to decrypt sensitive project data
const decryptProjectData = async (encryptedData) => {
  if (!ENABLE_ENCRYPTION || !encryptedData) return null;
  
  try {
    if (!isUserAuthenticated()) {
      console.warn('User not authenticated, cannot decrypt data');
      return null;
    }
    
    return await decryptData(encryptedData);
  } catch (error) {
    console.warn('Decryption failed, falling back to unencrypted data:', error);
    return null;
  }
};

export const saveProject = async (projectData) => {
  const { id, name, customer, customerAddress, customerPhone, type, status, equipment, conductors, mapCenter, mapZoom, mapStyle, viewport, mapSnapshot } = projectData;
  const timestamp = new Date().toISOString();

  try {
    // Ensure database is open
    const database = await getDatabase();
    
    // Validate and sanitize input data (optional)
    let sanitizedData = projectData;
    if (ENABLE_VALIDATION) {
      try {
        sanitizedData = sanitizeProjectData(projectData);
        const validation = validateProjectData(sanitizedData);
        
        if (!validation.isValid) {
          console.warn('Validation warnings:', validation.errors);
          // Don't throw error, just log warnings for now
        }
      } catch (error) {
        console.warn('Validation failed, using original data:', error);
        sanitizedData = projectData;
      }
    }
    
    // Try to encrypt sensitive data (optional)
    let encryptedData = null;
    try {
      encryptedData = await encryptProjectData(sanitizedData);
    } catch (error) {
      console.warn('Encryption failed, continuing without encryption:', error);
    }
    
    const projectId = await database.transaction('rw', [database.projects, database.mapState, database.equipment, database.conductors], async () => {
      // Save project with or without encrypted data
      const projectToSave = {
        id,
        name: sanitizedData.name,
        customer: sanitizedData.customer,
        type: sanitizedData.type,
        status: sanitizedData.status,
        updatedAt: timestamp,
        createdAt: id ? undefined : timestamp
      };
      
      // Add encrypted data if available
      if (encryptedData) {
        projectToSave.encryptedData = encryptedData;
      } else {
        // Fallback to storing sensitive data unencrypted (for backward compatibility)
        projectToSave.customerAddress = sanitizedData.customerAddress;
        projectToSave.customerPhone = sanitizedData.customerPhone;
      }
      
      // Add map snapshot if available
      if (mapSnapshot) {
        projectToSave.mapSnapshot = mapSnapshot;
      }
      
      const savedProjectId = await database.projects.put(projectToSave);

      // Save map state
      const mapStateToSave = {
        projectId: savedProjectId,
        center: mapCenter,
        zoom: mapZoom,
        style: mapStyle,
        viewport
      };
      await database.mapState.put(mapStateToSave);

      // For backward compatibility, also save equipment and conductors in separate tables
      await database.equipment.where('projectId').equals(savedProjectId).delete();
      await database.conductors.where('projectId').equals(savedProjectId).delete();
      
      if (sanitizedData.equipment && sanitizedData.equipment.length > 0) {
        const equipmentToSave = sanitizedData.equipment.map(item => ({
          ...item,
          projectId: savedProjectId
        }));
        await database.equipment.bulkAdd(equipmentToSave);
      }
      
      if (sanitizedData.conductors && sanitizedData.conductors.length > 0) {
        const conductorsToSave = sanitizedData.conductors.map(item => ({
          ...item,
          projectId: savedProjectId
        }));
        await database.conductors.bulkAdd(conductorsToSave);
      }

      return savedProjectId;
    });

    return projectId;
  } catch (error) {
    console.error('Error in saveProject:', error);
    throw error;
  }
};

export const loadProject = async (projectId) => {
  try {
    // Ensure database is open
    const database = await getDatabase();
    
    const project = await database.projects.get(projectId);
    
    if (!project) throw new Error('Project not found');

    const mapState = await database.mapState.get(projectId);
    
    // Try to decrypt sensitive data first
    let decryptedData = null;
    if (project.encryptedData && ENABLE_ENCRYPTION) {
      try {
        decryptedData = await decryptProjectData(project.encryptedData);
      } catch (error) {
        console.warn('Failed to decrypt data, falling back to unencrypted:', error);
        decryptedData = null;
      }
    }
    
    // If no decrypted data, use unencrypted data from project or separate tables
    if (!decryptedData) {
      // Always try to load from separate tables first
      const equipment = await database.equipment.where('projectId').equals(projectId).toArray();
      const conductors = await database.conductors.where('projectId').equals(projectId).toArray();
      
      decryptedData = {
        customerAddress: project.customerAddress,
        customerPhone: project.customerPhone,
        equipment: equipment || [],
        conductors: conductors || []
      };
    }

    const result = {
      ...project,
      customerAddress: decryptedData?.customerAddress,
      customerPhone: decryptedData?.customerPhone,
      mapCenter: mapState?.center,
      mapZoom: mapState?.zoom,
      mapStyle: mapState?.style,
      viewport: mapState?.viewport,
      mapSnapshot: project.mapSnapshot,
      equipment: decryptedData?.equipment || [],
      conductors: decryptedData?.conductors || []
    };
    
    return result;
  } catch (error) {
    console.error('Error loading project:', error);
    throw error;
  }
};

export const deleteProject = async (projectId) => {
  try {
    // Ensure database is open
    const database = await getDatabase();
    
    await database.transaction('rw', [database.projects, database.mapState, database.equipment, database.conductors], async () => {
      await Promise.all([
        database.projects.delete(projectId),
        database.mapState.delete(projectId),
        database.equipment.where('projectId').equals(projectId).delete(),
        database.conductors.where('projectId').equals(projectId).delete()
      ]);
    });
  } catch (error) {
    console.error('Error deleting project:', error);
    throw error;
  }
};

export const listProjects = async () => {
  try {
    // Ensure database is open
    const database = await getDatabase();
    
    const projects = await database.projects.orderBy('updatedAt').reverse().toArray();
    return projects;
  } catch (error) {
    console.error('Error listing projects:', error);
    throw error;
  }
};

export { getDatabase };
export default {
  getDatabase: async () => await getDatabase(),
  saveProject,
  loadProject,
  deleteProject,
  listProjects,
  switchUserDatabase
}; 